import { startCloudflareRun, type CloudflareRun, type CloudflareRunHandle } from './cloudflare-run';
import { runMlabReference, type MlabRun } from './mlab-run';
import { runUploadAb, type UploadAbRun } from './upload-ab';
import { runUploadMethodExperiment, type UploadMethodRun, type VariantName } from './upload-method';

type AnyRun = CloudflareRun | MlabRun | UploadAbRun | UploadMethodRun;

const STORAGE_KEY = 'measurement-spike/runs/v1';

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}

const ui = {
  label: el<HTMLInputElement>('label'),
  runCf: el<HTMLButtonElement>('run-cf'),
  cancelCf: el<HTMLButtonElement>('cancel-cf'),
  status: el<HTMLParagraphElement>('status'),
  metrics: el<HTMLTableSectionElement>('metrics-body'),
  requests: el<HTMLUListElement>('requests-summary'),
  errors: el<HTMLUListElement>('errors'),
  runs: el<HTMLTableSectionElement>('runs-body'),
  exportJson: el<HTMLButtonElement>('export-json'),
  toggleJson: el<HTMLButtonElement>('toggle-json'),
  clearRuns: el<HTMLButtonElement>('clear-runs'),
  jsonOutput: el<HTMLTextAreaElement>('json-output'),
  mlabConsent: el<HTMLInputElement>('mlab-consent'),
  runMlab: el<HTMLButtonElement>('run-mlab'),
  mlabStatus: el<HTMLParagraphElement>('mlab-status'),
  // TEMPORARY upload A/B experiment
  abReceiver: el<HTMLSpanElement>('ab-receiver'),
  abFastCom: el<HTMLInputElement>('ab-fastcom'),
  runAb: el<HTMLButtonElement>('run-ab'),
  abStatus: el<HTMLParagraphElement>('ab-status'),
  abBody: el<HTMLTableSectionElement>('ab-body'),
  // TEMPORARY upload-method experiment
  meTarget: el<HTMLSelectElement>('me-target'),
  meFastCom: el<HTMLInputElement>('me-fastcom'),
  runMe: el<HTMLButtonElement>('run-me'),
  meStatus: el<HTMLParagraphElement>('me-status'),
  meBody: el<HTMLTableSectionElement>('me-body'),
};

let runs: AnyRun[] = loadRuns();
let active: CloudflareRunHandle | undefined;
let busy = false;

function loadRuns(): AnyRun[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as AnyRun[]) : [];
  } catch {
    return [];
  }
}

function saveRuns(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(runs));
  } catch {
    // Storage unavailable (private mode or quota): runs stay in memory and can still be exported.
  }
}

const fmt = (value: number | null | undefined, digits = 1): string =>
  value === null || value === undefined ? '—' : value.toFixed(digits);

function row(cells: string[]): HTMLTableRowElement {
  const tr = document.createElement('tr');
  for (const text of cells) {
    const td = document.createElement('td');
    td.textContent = text;
    tr.append(td);
  }
  return tr;
}

function listItems(target: HTMLUListElement, items: string[]): void {
  target.replaceChildren(
    ...items.map((text) => {
      const li = document.createElement('li');
      li.textContent = text;
      return li;
    }),
  );
}

function renderCurrent(run: CloudflareRun): void {
  const m = run.metrics;
  const p = run.points;
  ui.metrics.replaceChildren(
    row(['Download', `${fmt(m.downloadMbps, 2)} Mbps`, `${p.download.length} samples`]),
    row(['Upload', `${fmt(m.uploadMbps, 2)} Mbps`, `${p.upload.length} samples`]),
    row(['Idle latency', `${fmt(m.idleLatencyMs)} ms`, `${p.idleLatencyMs.length} samples`]),
    row(['Idle jitter', `${fmt(m.idleJitterMs)} ms`, '']),
    row(['Loaded latency (download)', `${fmt(m.downloadLoadedLatencyMs)} ms`, `jitter ${fmt(m.downloadLoadedJitterMs)} ms · ${p.downloadLoadedLatencyMs.length} samples`]),
    row(['Loaded latency (upload)', `${fmt(m.uploadLoadedLatencyMs)} ms`, `jitter ${fmt(m.uploadLoadedJitterMs)} ms · ${p.uploadLoadedLatencyMs.length} samples`]),
    row(['Total duration (wall clock)', `${fmt(run.wallClockMs === null ? null : run.wallClockMs / 1000, 2)} s`, `engine: ${fmt(run.engineTotalDurationMs === null ? null : run.engineTotalDurationMs / 1000, 2)} s`]),
  );

  const counts = new Map<string, number>();
  for (const request of run.requests) {
    const during = new URLSearchParams(request.query).get('during');
    const key = `${request.path}${during ? ` (during=${during})` : ''}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  // Chromium records requests the engine aborts at a phase boundary with status 0 and no timing.
  const aborted = run.requests.filter((r) => r.responseStatus === 0 || (r.durationMs === 0 && r.transferSize === 0));
  const completed = run.requests.filter((r) => !aborted.includes(r));
  const protocols = [...new Set(completed.map((r) => r.protocol || 'not reported'))];
  const compressedDownloads = completed.filter(
    (r) => r.path === '/down' && r.decodedBodySize > 0 && r.encodedBodySize !== r.decodedBodySize,
  ).length;
  const zeroTransfer = completed.filter((r) => r.transferSize === 0).length;
  const unexpectedStatus = completed.filter(
    (r) => r.responseStatus !== null && r.responseStatus !== 200 && r.responseStatus !== 204,
  ).length;
  listItems(ui.requests, [
    `Requests captured: ${run.requests.length} (${aborted.length} aborted by the engine at phase boundaries)`,
    ...[...counts].map(([key, count]) => `${key}: ${count}`),
    `Protocols: ${protocols.join(', ') || '—'}`,
    `Compressed download responses (encoded ≠ decoded): ${compressedDownloads}`,
    `Completed responses with transferSize 0 (not reported by browser): ${zeroTransfer}`,
    `Unexpected HTTP statuses (Chromium only): ${unexpectedStatus}`,
  ]);
  listItems(
    ui.errors,
    run.errors.length ? run.errors.map((e) => `${fmt(e.atMs / 1000, 2)} s: ${e.message}${e.status ? ` (HTTP ${e.status})` : ''}`) : ['None'],
  );
}

// Invalid runs keep raw data in the export but never show a throughput number.
const abMbps = (run: UploadAbRun, target: 'worker' | 'receiver') =>
  run.status === 'valid' ? (run.results.find((r) => r.target === target)?.mbps ?? null) : null;

function vsFastCom(value: number | null, fastCom: number | null): string {
  if (value === null || !fastCom) return '—';
  const pct = (value / fastCom - 1) * 100;
  return `${pct >= 0 ? '+' : '−'}${Math.abs(pct).toFixed(0)}%`;
}

function renderAb(): void {
  const abRuns = runs.filter((r): r is UploadAbRun => r.kind === 'upload-ab');
  ui.abBody.replaceChildren(
    ...[...abRuns].reverse().map((run) => {
      const worker = abMbps(run, 'worker');
      const receiver = abMbps(run, 'receiver');
      return row([
        new Date(run.startedAt).toLocaleTimeString(),
        run.label,
        fmt(worker),
        fmt(receiver),
        fmt(run.fastComUploadMbps),
        vsFastCom(worker, run.fastComUploadMbps),
        vsFastCom(receiver, run.fastComUploadMbps),
        `${fmt(run.receiverPingMs)} ms`,
        `CF ${run.cloudflareColo ?? '?'} · receiver ${run.receiverRegion ?? '?'}`,
        run.status === 'valid' ? 'valid' : `INVALID — rerun (${(run.invalidReasons ?? []).join('; ')})`,
      ]);
    }),
  );
}

// Invalid runs keep raw data in the export but never show a throughput number.
const meResult = (run: UploadMethodRun, variant: VariantName) =>
  run.status === 'valid' ? run.results.find((r) => r.variant === variant) : undefined;

function renderMethod(): void {
  const methodRuns = runs.filter((r): r is UploadMethodRun => r.kind === 'upload-method');
  ui.meBody.replaceChildren(
    ...[...methodRuns].reverse().map((run) => {
      const [a, b, c] = (['A', 'B', 'C'] as const).map((v) => meResult(run, v));
      const chunk = (r: typeof a) => (r ? `${r.maxChunkBytes / 1048576}` : '—');
      return row([
        new Date(run.startedAt).toLocaleTimeString(),
        run.label,
        run.target === 'worker' ? 'Worker' : 'Fly',
        run.order.join('→'),
        fmt(a?.mbps),
        fmt(b?.mbps),
        fmt(b?.aWindowMbps),
        fmt(c?.mbps),
        fmt(run.fastComUploadMbps),
        vsFastCom(a?.mbps ?? null, run.fastComUploadMbps),
        vsFastCom(b?.mbps ?? null, run.fastComUploadMbps),
        vsFastCom(c?.mbps ?? null, run.fastComUploadMbps),
        `${chunk(a)} / ${chunk(b)} / ${chunk(c)} MiB`,
        run.status === 'valid' ? 'valid' : `INVALID — rerun (${run.invalidReasons.join('; ')})`,
      ]);
    }),
  );
}

function runCells(run: AnyRun): string[] {
  const time = new Date(run.startedAt).toLocaleTimeString();
  const seconds = fmt(run.wallClockMs === null ? null : run.wallClockMs / 1000, 1);
  if (run.kind === 'upload-method') {
    const [a, b, c] = (['A', 'B', 'C'] as const).map((v) => fmt(meResult(run, v)?.mbps));
    return [time, 'Upload method (temp)', run.label, run.status, '—', `A ${a} · B ${b} · C ${c}`, '—', '—', '—', seconds];
  }
  if (run.kind === 'upload-ab') {
    return [time, 'Upload A/B (temp)', run.label, run.status, '—', `W ${fmt(abMbps(run, 'worker'))} · R ${fmt(abMbps(run, 'receiver'))}`, '—', '—', '—', seconds];
  }
  if (run.kind === 'cloudflare-engine') {
    const m = run.metrics;
    return [time, 'Cloudflare engine', run.label, run.status, fmt(m.downloadMbps), fmt(m.uploadMbps), fmt(m.idleLatencyMs), fmt(m.downloadLoadedLatencyMs), fmt(m.uploadLoadedLatencyMs), seconds];
  }
  return [
    time,
    'M-Lab NDT7 (ref)',
    run.label,
    run.status,
    fmt(run.download?.meanClientMbps),
    fmt(run.upload?.meanClientMbps),
    `minRTT ${fmt(run.download?.serverMinRttMs)}`,
    `TCP RTT ${fmt(run.download?.serverRttMs)}`,
    '—',
    seconds,
  ];
}

function renderRuns(): void {
  ui.runs.replaceChildren(...[...runs].reverse().map((run) => row(runCells(run))));
  renderAb();
  renderMethod();
  if (!ui.jsonOutput.hidden) ui.jsonOutput.value = exportText();
}

function exportText(): string {
  return JSON.stringify({ schema: 'measurement-spike/export/v1', exportedAt: new Date().toISOString(), runs }, null, 2);
}

function setBusy(value: boolean): void {
  busy = value;
  ui.runCf.disabled = value;
  ui.runMlab.disabled = value || !ui.mlabConsent.checked;
  ui.runAb.disabled = value || !__RECEIVER_URL__;
  ui.runMe.disabled = value;
  ui.cancelCf.disabled = !active;
}

function describePhase(run: CloudflareRun): string {
  const phase = run.phases.at(-1);
  if (!phase) return 'Starting…';
  const detail = phase.type === 'latency' ? `${phase.numPackets} pings` : `${phase.bytes} B × ${phase.count}`;
  return `Step ${phase.index + 1}: ${phase.type} (${detail})`;
}

ui.runCf.addEventListener('click', () => {
  if (busy) return;
  active = startCloudflareRun(ui.label.value.trim(), (run) => {
    renderCurrent(run);
    ui.status.textContent = run.status === 'running' ? describePhase(run) : `Cloudflare engine run ${run.status}.`;
  });
  setBusy(true);
  void active.done.then((run) => {
    active = undefined;
    runs.push(run);
    saveRuns();
    renderRuns();
    setBusy(false);
  });
});

ui.cancelCf.addEventListener('click', () => active?.cancel());

ui.mlabConsent.addEventListener('change', () => setBusy(busy));

ui.runMlab.addEventListener('click', () => {
  if (busy || !ui.mlabConsent.checked) return;
  setBusy(true);
  ui.mlabStatus.textContent = 'Running M-Lab NDT7 reference test (about 20–25 s; cannot be cancelled)…';
  void runMlabReference(ui.label.value.trim(), true).then((run) => {
    runs.push(run);
    saveRuns();
    renderRuns();
    ui.mlabStatus.textContent = `M-Lab reference run ${run.status}${run.errors.length ? `: ${run.errors.map((e) => e.message).join('; ')}` : '.'}`;
    setBusy(false);
  });
});

ui.exportJson.addEventListener('click', () => {
  const blob = new Blob([exportText()], { type: 'application/json' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `measurement-spike-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
});

ui.toggleJson.addEventListener('click', () => {
  ui.jsonOutput.hidden = !ui.jsonOutput.hidden;
  ui.toggleJson.textContent = ui.jsonOutput.hidden ? 'Show JSON' : 'Hide JSON';
  if (!ui.jsonOutput.hidden) ui.jsonOutput.value = exportText();
});

ui.clearRuns.addEventListener('click', () => {
  if (!confirm('Delete all stored runs from this browser?')) return;
  runs = [];
  saveRuns();
  renderRuns();
});

// TEMPORARY upload A/B experiment
ui.abReceiver.textContent = __RECEIVER_URL__ || 'not configured in this build';
ui.runAb.addEventListener('click', () => {
  if (busy || !__RECEIVER_URL__) return;
  const fastCom = Number(ui.abFastCom.value);
  const previousAbRuns = runs.filter((r) => r.kind === 'upload-ab').length;
  setBusy(true);
  ui.abStatus.textContent = 'Running upload A/B (about 15 s)…';
  void runUploadAb({
    label: ui.label.value.trim(),
    receiverUrl: __RECEIVER_URL__,
    fastComUploadMbps: Number.isFinite(fastCom) && fastCom > 0 ? fastCom : null,
    // Alternate which target goes first so slow drift in the connection doesn't favour one target.
    receiverFirst: previousAbRuns % 2 === 1,
    onStatus: (message) => {
      ui.abStatus.textContent = message;
    },
  }).then((run) => {
    runs.push(run);
    saveRuns();
    renderRuns();
    ui.abStatus.textContent =
      run.status === 'valid'
        ? `Upload A/B valid (${run.order.join(' → ')}, ${fmt(run.wallClockMs / 1000, 1)} s).`
        : `Upload A/B INVALID — please run it again. ${run.invalidReasons.join('; ')}`;
  })
    .catch((error: unknown) => {
      ui.abStatus.textContent = `Upload A/B failed: ${String(error)}`;
    })
    .finally(() => setBusy(false));
});

// TEMPORARY upload-method experiment
ui.runMe.addEventListener('click', () => {
  if (busy) return;
  const target = ui.meTarget.value === 'receiver' ? 'receiver' : 'worker';
  if (target === 'receiver' && !__RECEIVER_URL__) {
    ui.meStatus.textContent = 'The Fly receiver is not configured in this build; choose the Cloudflare Worker.';
    return;
  }
  const fastCom = Number(ui.meFastCom.value);
  const previous = runs.filter((r) => r.kind === 'upload-method').length;
  setBusy(true);
  ui.meStatus.textContent = 'Running upload method experiment (about 25 s)…';
  void runUploadMethodExperiment({
    label: ui.label.value.trim(),
    target,
    receiverUrl: __RECEIVER_URL__,
    fastComUploadMbps: Number.isFinite(fastCom) && fastCom > 0 ? fastCom : null,
    rotationIndex: previous,
    onStatus: (message) => {
      ui.meStatus.textContent = message;
    },
  })
    .then((run) => {
      runs.push(run);
      saveRuns();
      renderRuns();
      ui.meStatus.textContent =
        run.status === 'valid'
          ? `Upload method experiment valid (${run.order.join(' → ')}, ${fmt(run.wallClockMs / 1000, 1)} s).`
          : `Upload method experiment INVALID — please run it again. ${run.invalidReasons.join('; ')}`;
    })
    .catch((error: unknown) => {
      ui.meStatus.textContent = `Upload method experiment failed: ${String(error)}`;
    })
    .finally(() => setBusy(false));
});

renderRuns();
setBusy(false);
